import { supabase } from '@/core/database/supabase';
import { storageService } from '@/services/storage.service';

export interface DocumentFolder {
  id: string;
  name: string;
  parent_id: string | null;
  company_id: string;
  created_at: string;
}

export interface DocumentFile {
  id: string;
  title: string;
  file_name: string;
  document_type: string;
  file_size_bytes: number;
  folder_id: string | null;
  company_id: string;
  created_at: string;
  is_shared: boolean;
  created_by: string;
  user_profile?: {
    full_name: string;
    avatar_path: string | null;
  };
}

export const documentsService = {
  /** Lấy danh sách thư mục gốc (hoặc thư mục con nếu truyền parentId) */
  async getFolders(companyId: string, parentId: string | null = null, projectId: string | null = null): Promise<DocumentFolder[]> {
    let query = supabase
      .from('document_folders')
      .select('*')
      .eq('company_id', companyId);
      
    if (projectId === 'all') {
      // Don't filter by project
    } else if (projectId) {
      query = query.eq('project_id', projectId);
    } else {
      query = query.is('project_id', null);
    }

    if (parentId) {
      query = query.eq('parent_id', parentId);
    } else {
      query = query.is('parent_id', null);
    }

    const { data, error } = await query.order('name', { ascending: true });
    if (error) throw error;
    return data || [];
  },

  /** Lấy danh sách tài liệu trong một thư mục */
  async getDocuments(companyId: string, folderId: string | null = null, projectId: string | null = null): Promise<DocumentFile[]> {
    let query = supabase
      .from('documents')
      .select(`
        *,
        user_profile:user_profiles!created_by(full_name, avatar_path)
      `)
      .eq('company_id', companyId);

    if (projectId === 'all') {
      // Don't filter by project
    } else if (projectId) {
      query = query.eq('project_id', projectId);
    } else {
      query = query.is('project_id', null);
    }

    if (folderId) {
      query = query.eq('folder_id', folderId);
    } else {
      query = query.is('folder_id', null);
    }

    const { data, error } = await query.order('created_at', { ascending: false });
    if (error) throw error;
    
    // Map data
    return (data || []).map(row => ({
      ...row,
      user_profile: row.user_profile ? (Array.isArray(row.user_profile) ? row.user_profile[0] : row.user_profile) : undefined
    })) as DocumentFile[];
  },

  /** Khởi tạo cấu trúc thư mục chuẩn cho công ty hoặc dự án */
  async seedStandardFolders(companyId: string, userId: string, projectId: string | null = null) {
    const standardFolders = [
      { name: '01. Hồ sơ Pháp lý & Hợp đồng' },
      { name: '02. Hồ sơ Thiết kế & Kỹ thuật' },
      { name: '03. Biện pháp Thi công & QA/QC' },
      { name: '04. Quản lý Tài chính & Khối lượng' },
      { name: '05. Nhật ký & Báo cáo Hiện trường' }
    ];

    for (const folder of standardFolders) {
      await supabase.from('document_folders').insert({
        name: folder.name,
        company_id: companyId,
        project_id: projectId === 'all' ? null : projectId,
        created_by: userId
      });
    }
  },

  /** Tải file và lưu database */
  async uploadDocuments(files: File[], companyId: string, folderId: string | null, userId: string, projectId: string | null = null) {
    if (!files.length) return 0;
    
    // 1. Tải lên Storage
    const results = await storageService.uploadMultiple(files, {
      bucket: 'qa-documents',
      folder: `documents/${companyId}${folderId ? `/${folderId}` : ''}`
    });

    const validResults = results.map((res, idx) => ({ ...res, file: files[idx] })).filter(r => !r.error);
    if (validResults.length === 0) throw new Error('Không có file nào được tải lên thành công');

    // 2. Lưu vào CSDL
    const records = validResults.map(r => ({
      company_id: companyId,
      folder_id: folderId,
      project_id: projectId === 'all' ? null : projectId,
      created_by: userId,
      title: r.file.name,
      file_name: r.file.name,
      file_path: r.path,
      mime_type: r.file.type || 'application/octet-stream',
      file_size_bytes: r.file.size,
      document_type: r.file.name.split('.').pop()?.toLowerCase() || 'unknown',
      status_code: 'active'
    }));

    const { error } = await supabase.from('documents').insert(records);
    if (error) throw error;
    
    return validResults.length;
  }
};
